import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import contractComplianceService from '../../../services/contractComplianceService';

const ContractComplianceScanner = ({ contract, onScanComplete }) => {
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [error, setError] = useState('');

  const handleScan = async () => {
    try {
      setScanning(true);
      setError('');
      const results = await contractComplianceService?.scanContract(contract);
      setScanResults(results);
      if (onScanComplete) {
        onScanComplete(results);
      }
    } catch (err) {
      setError(err?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'compliant':
        return {
          bg: 'bg-success/10',
          text: 'text-success',
          icon: 'CheckCircle',
          badge: 'bg-success text-success-foreground'
        };
      case 'needs_improvement':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          icon: 'AlertCircle',
          badge: 'bg-warning text-warning-foreground'
        };
      case 'non_compliant':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          icon: 'XCircle',
          badge: 'bg-error text-error-foreground'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'AlertCircle',
          badge: 'bg-muted text-muted-foreground'
        };
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error/10 border-error/20 text-error';
      case 'high':
        return 'bg-warning/10 border-warning/20 text-warning';
      case 'medium':
        return 'bg-accent/10 border-accent/20 text-accent';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  if (!scanResults) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="bg-primary/10 rounded-lg p-3">
            <Icon name="Shield" size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              PNG Employment Act 1978 Compliance Scanner
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Auto-scan this contract against Employment Act 1978 clauses, flag missing provisions, and receive compliance improvement suggestions before finalization.
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
              <Icon name="Info" size={14} />
              <span>Minimum wage: K5.00 per hour (effective 2026-01-01)</span>
            </div>
            {error && (
              <div className="bg-error/10 border border-error text-error px-3 py-2 rounded-md mb-4 text-sm">
                {error}
              </div>
            )}
            <Button
              onClick={handleScan}
              disabled={scanning}
              iconName={scanning ? 'Loader2' : 'Search'}
              iconPosition="left"
            >
              {scanning ? 'Scanning Contract...' : 'Scan Contract for Compliance'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const statusStyles = getStatusStyles(scanResults?.complianceStatus);

  return (
    <div className="space-y-4">
      {/* Compliance Summary */}
      <div className={`${statusStyles?.bg} border border-current/20 rounded-lg p-6`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Icon name={statusStyles?.icon} size={32} className={statusStyles?.text} />
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-1">
                Compliance Status: {scanResults?.complianceStatus?.replace('_', ' ')?.toUpperCase()}
              </h3>
              <p className="text-sm text-muted-foreground">
                Scanned against PNG Employment Act 1978 requirements
              </p>
            </div>
          </div>
          <span className={`${statusStyles?.badge} px-3 py-1 text-sm font-semibold rounded-md`}>
            {scanResults?.complianceScore}% Compliant
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-background/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Required Clauses</p>
            <p className="text-2xl font-bold text-foreground">{scanResults?.totalRequiredClauses}</p>
          </div>
          <div className="bg-background/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Present</p>
            <p className="text-2xl font-bold text-success">{scanResults?.presentClausesCount}</p>
          </div>
          <div className="bg-background/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Missing</p>
            <p className="text-2xl font-bold text-error">{scanResults?.missingClausesCount}</p>
          </div>
          <div className="bg-background/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Critical Issues</p>
            <p className="text-2xl font-bold text-error">{scanResults?.criticalViolations}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="DollarSign" size={14} />
          <span>
            Minimum Wage: K{scanResults?.minimumWageRate?.toFixed(2)}/hour 
            {scanResults?.wageCompliant ? (
              <span className="text-success ml-2">✓ Compliant</span>
            ) : (
              <span className="text-error ml-2">✗ Non-Compliant</span>
            )}
          </span>
        </div>
      </div>

      {/* Violations and Missing Provisions */}
      {scanResults?.violations?.length > 0 && (
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="AlertTriangle" size={20} className="text-error" />
            <h3 className="text-lg font-semibold text-foreground">
              Compliance Issues ({scanResults?.violations?.length})
            </h3>
          </div>
          <div className="space-y-3">
            {scanResults?.violations?.map((violation, idx) => (
              <div
                key={idx}
                className={`border rounded-lg p-4 ${getSeverityStyles(violation?.severity)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm">{violation?.title}</h4>
                      <span className="px-2 py-0.5 text-xs font-semibold rounded uppercase">
                        {violation?.severity}
                      </span>
                    </div>
                    <p className="text-xs mb-2">{violation?.description}</p>
                  </div>
                </div>
                {violation?.suggestion && (
                  <div className="bg-background/50 rounded-md p-3 mt-2">
                    <div className="flex items-start gap-2">
                      <Icon name="Lightbulb" size={14} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium mb-1">Compliance Improvement:</p>
                        <p className="text-xs">{violation?.suggestion}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Present Provisions */}
      {scanResults?.presentProvisions?.length > 0 && (
        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <h3 className="text-lg font-semibold text-foreground">
              Compliant Provisions ({scanResults?.presentProvisions?.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {scanResults?.presentProvisions?.map((provision, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Check" size={14} className="text-success flex-shrink-0" />
                <span>{provision?.clauseName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={handleScan}
          variant="outline"
          iconName="RefreshCw"
          iconPosition="left"
        >
          Re-scan Contract
        </Button>
        {scanResults?.complianceStatus === 'compliant' && (
          <Button
            variant="default"
            iconName="CheckCircle"
            iconPosition="left"
          >
            Approve & Finalize Contract
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContractComplianceScanner;
